/**
 * Chart colours, read from the design tokens at runtime.
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 * See docs/decisions/ADR-057-colour-means-status.md
 *
 * Recharts takes a colour in a prop and writes it to an SVG presentation
 * attribute, where a `var(--x)` reference is not valid. So instead of
 * duplicating hex values in TypeScript — which would drift from index.css the
 * first time anyone retuned a token — this resolves the custom properties
 * from the live document. index.css stays the single source.
 *
 * WHY A MutationObserver AND NOT `useMemo([theme])`.
 * That was the first attempt and it silently served stale colours: the theme
 * class is applied by ThemeContext in an effect, which runs AFTER the render
 * that reads `theme`. So a memo keyed on `theme` calls getComputedStyle while
 * the old class is still on <html> and resolves the previous mode's palette —
 * charts kept their light colours in dark mode. Effect ordering between
 * components is not something to rely on either, so this watches the class
 * attribute itself and re-resolves once it actually changes.
 *
 * Two palettes, and they are not interchangeable:
 *
 *   series  categorical identity. Assign in fixed order, never cycled. Slots
 *           1-3 are validated all-pairs against this app's chart surfaces in
 *           both modes; there is no slot 4, because past three the reference
 *           palette stops clearing the colour-vision floors. A fourth
 *           category folds into "Other" or the chart facets.
 *
 *   status  a verdict, and reserved. Never spent on "series 4". Every chart
 *           using it must also label the marks, so the reading never depends
 *           on hue alone.
 */
import { useEffect, useState } from 'react';

export interface ChartColors {
  /** Categorical slots, in fixed order. Index 0 is slot 1. */
  series: string[];
  /** Reserved status colours, for charts whose marks ARE verdicts. */
  status: {
    pass: string;
    fail: string;
    active: string;
    inert: string;
  };
}


/** Values matching the light-mode tokens, used before the first resolve and
 *  during SSR/tests where there is no computed style to read. */
const FALLBACK: ChartColors = {
  series: ['#2a78d6', '#eb6834', '#1baf7a'],
  status: { pass: '#047C56', fail: '#BF123D', active: '#1450D2', inert: '#65758B' },
};

function resolve(): ChartColors {
  if (typeof document === 'undefined') return FALLBACK;
  const root = getComputedStyle(document.documentElement);
  const raw = (name: string) => root.getPropertyValue(name).trim();
  /** A hex custom property, used as-is. */
  const hex = (name: string, fallback: string) => raw(name) || fallback;
  /** An `H S% L%` triplet needs wrapping before it is a colour. */
  const hsl = (name: string, fallback: string) => (raw(name) ? `hsl(${raw(name)})` : fallback);

  return {
    series: [
      hex('--chart-series-1', FALLBACK.series[0]),
      hex('--chart-series-2', FALLBACK.series[1]),
      hex('--chart-series-3', FALLBACK.series[2]),
    ],
    status: {
      pass: hsl('--qc-success', FALLBACK.status.pass),
      fail: hsl('--qc-error', FALLBACK.status.fail),
      active: hsl('--qc-info', FALLBACK.status.active),
      inert: hsl('--border-strong', FALLBACK.status.inert),
    },
  };
}

/**
 * Map an aggregated job-status bucket onto its reserved colour.
 *
 * The analytics endpoint buckets the nine job statuses into a handful of
 * names and used to send a `color` for each one — so the palette lived in the
 * backend, out of reach of the token system and of any contrast check. The
 * name is the contract now; the colour is chosen here.
 */
export function statusColor(status: string, colors: ChartColors): string {
  const key = (status ?? '').toLowerCase();
  if (key.includes('fail') || key.includes('error')) return colors.status.fail;
  if (key.includes('complete')) return colors.status.pass;
  if (
    key.includes('active') ||
    key.includes('process') ||
    key.includes('queue') ||
    key.includes('pending')
  ) {
    return colors.status.active;
  }
  return colors.status.inert;
}

export function useChartColors(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolve);

  useEffect(() => {
    // Resolve once on mount (the class may already differ from first paint),
    // then again whenever the theme class actually lands on <html>.
    setColors(resolve());

    const observer = new MutationObserver(() => setColors(resolve()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return colors;
}
